import React, { useState } from 'react';
import { Button } from '../button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../dialog';
import { Delete, Space } from 'lucide-react';

interface TextKeypadProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title: string;
  initialValue?: string;
  placeholder?: string;
}

export const TextKeypad: React.FC<TextKeypadProps> = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  title,
  initialValue = '',
  placeholder = 'Ingrese texto...'
}) => {
  const [displayValue, setDisplayValue] = useState<string>(initialValue);

  const handleKeyClick = (key: string) => {
    setDisplayValue(displayValue + key.toUpperCase());
  };

  const handleSpecialKeyClick = (key: string) => {
    switch (key) {
      case 'space':
        setDisplayValue(displayValue + ' ');
        break;
      case 'backspace':
        handleBackspace();
        break;
      case 'clear':
        handleClear();
        break;
    }
  };

  const handleBackspace = () => {
    if (displayValue.length > 0) {
      setDisplayValue(displayValue.slice(0, -1));
    }
  };

  const handleClear = () => {
    setDisplayValue('');
  };

  const handleSubmit = () => {
    onSubmit(displayValue);
    onClose();
  };

  const handleClose = () => {
    setDisplayValue(initialValue);
    onClose();
  };

  // Reset display value when modal opens with initial value
  React.useEffect(() => {
    if (isOpen) {
      setDisplayValue(initialValue);
    }
  }, [isOpen, initialValue]);

  const keyboardRows = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ñ'],
    ['z', 'x', 'c', 'v', 'b', 'n', 'm', 'backspace']
  ];

  const numbersRow = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
  const symbolsRow = ['@', '.', ',', '-', '_', '#', '&', '(', ')', '%'];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg text-center">{title}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Display */}
          <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
            <div className="text-left text-lg text-gray-800 min-h-[2.5rem] flex items-center">
              {displayValue}
              <span className="animate-pulse">|</span>
            </div>
          </div>

          {/* QWERTY Keyboard */}
          <div className="space-y-2">
            {/* Numbers row */}
            <div className="grid grid-cols-10 gap-2">
              {numbersRow.map((number, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-gray-50 hover:bg-gray-100 border-gray-300"
                  onClick={() => setDisplayValue(displayValue + number)}
                >
                  {number}
                </Button>
              ))}
            </div>

            {/* First row - QWERTY */}
            <div className="grid grid-cols-10 gap-2">
              {keyboardRows[0].map((key, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-white hover:bg-gray-50 border-gray-300"
                  onClick={() => handleKeyClick(key)}
                >
                  {key.toUpperCase()}
                </Button>
              ))}
            </div>

            {/* Second row - ASDF */}
            <div className="grid grid-cols-10 gap-2">
              {keyboardRows[1].map((key, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-white hover:bg-gray-50 border-gray-300"
                  onClick={() => handleKeyClick(key)}
                >
                  {key.toUpperCase()}
                </Button>
              ))}
            </div>

            {/* Third row - ZXCV with Backspace */}
            <div className="grid grid-cols-8 gap-2">
              {keyboardRows[2].map((key, index) => {
                if (key === 'backspace') {
                  return (
                    <Button
                      key={index}
                      variant="outline"
                      className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700"
                      onClick={() => handleSpecialKeyClick('backspace')}
                    >
                      <Delete className="h-4 w-4" />
                    </Button>
                  );
                }
                
                return (
                  <Button
                    key={index}
                    variant="outline"
                    className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-white hover:bg-gray-50 border-gray-300"
                    onClick={() => handleKeyClick(key)}
                  >
                    {key.toUpperCase()}
                  </Button>
                );
              })}
            </div>

            {/* Symbols row */}
            <div className="grid grid-cols-10 gap-2">
              {symbolsRow.map((symbol, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="h-12 text-sm font-semibold transition-all duration-150 active:scale-95 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700"
                  onClick={() => setDisplayValue(displayValue + symbol)}
                >
                  {symbol}
                </Button>
              ))}
            </div>

            {/* Bottom row - Space bar and actions */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                className="h-14 text-base font-semibold transition-all duration-150 active:scale-95 bg-red-50 hover:bg-red-100 border-red-200 text-red-700"
                onClick={() => handleSpecialKeyClick('clear')}
              >
                Limpiar
              </Button>

              <Button
                variant="outline"
                className="h-14 text-base font-semibold transition-all duration-150 active:scale-95 bg-gray-100 hover:bg-gray-200 border-gray-300"
                onClick={() => handleSpecialKeyClick('space')}
              >
                <Space className="h-4 w-4 mr-2" />
                Espacio
              </Button>

              <Button
                className="h-14 text-base font-semibold bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={handleSubmit}
              >
                Confirmar
              </Button>
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
};